import { UserRoleEnum } from '~enums';
import { OffsetPagedResults } from '~interfaces';
import { SkcOffsetPagedResponse } from '~interfaces/responses';
import ApiError from '~services/apiError';
import SkopeiConnectService from '~services/skopeiConnectService';

import User from '../../../interfaces/user';

class UsersService extends SkopeiConnectService {
	// All user endpoints use internal now. Could be moved to the base url at some point potentially.
	// Depending if we stick to calling it internal, or we get a different url when BFF is fully
	// implemented on the backend.
	protected readonly path = 'users';

	/**
	 * Gets a list of users.
	 *
	 * @param page The page.
	 * @param pageSize The page size.
	 * @param searchQuery Text to search for in the key name, hardware id or bluetooth name.
	 * @param filterOrganisationId Filter on organisation id.
	 * @param userRole A Filter on roles.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns An array of Users
	 */
	public async getUsers(
		params: {
			page: string;
			pageSize: string;
			searchQuery?: string;
			filterOrganisationId?: string;
			userRole?: string;
		},
		signal?: AbortSignal
	): Promise<OffsetPagedResults<User>> {
		const endpoint = this.createEndpoint('v2', [], true);
		return await this.client
			.get<SkcOffsetPagedResponse<UserResponse>>(endpoint, {
				signal: signal,
				params: {
					'client-id': params.filterOrganisationId,
					search: params.searchQuery?.trim() || undefined,
					'page-number': params.page,
					'page-size': params.pageSize,
					role: params.userRole,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.reject(ApiError.createFromCustomCode('exportCompletedWithoutJobId'));
				}
				return Promise.resolve(this.mapSkcOffsetPagedResponse(response.data, this.fromResponse));
			});
	}

	public getUsersCacheKey(
		searchParams: URLSearchParams,
		overwrittenClientId?: string | undefined
	): (string | URLSearchParams | undefined)[] {
		return [this.path, searchParams.toString(), overwrittenClientId];
	}

	/**
	 * Gets a user by their id.
	 *
	 * @param userId The id of the user.
	 * @param overwrittenClientId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns The user.
	 */
	public async getUserById(
		params: { userId: string; overwrittenClientId?: string | undefined | null },
		signal?: AbortSignal
	): Promise<User> {
		const endpoint = this.createEndpoint('v2', [params.userId], true);
		return await this.client
			.get<UserResponse>(endpoint, {
				signal: signal,
				headers: {
					OverwrittenClientId: params.overwrittenClientId,
				},
			})
			.then((response) => {
				if (!response.data) {
					return Promise.reject(ApiError.createFromCustomCode('unexpectedEmptyResponse'));
				}
				return Promise.resolve(this.fromResponse(response.data));
			});
	}

	public getUserByIdCacheKey(
		userId: string,
		overwrittenClientId?: string | undefined
	): (string | undefined)[] {
		return [this.path, userId, overwrittenClientId] as const;
	}

	/**
	 * Deletes a user by their id.
	 *
	 * @param userId The id of the user.
	 * @param overwrittenClientId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 */
	async deleteUser(userId: string, organisationId?: string | null | undefined) {
		const endpoint = this.createEndpoint('v2', [userId], true);
		return await this.client.delete(endpoint, {
			headers: {
				OverwrittenClientId: organisationId,
			},
		});
	}

	/**
	 * Gets the currently signed in user.
	 *
	 * @param signal Signal for aborting the call.
	 *
	 * @returns The User that is signed in.
	 */
	async getCurrentUser(signal?: AbortSignal): Promise<User> {
		const endpoint = this.createEndpoint('v2', ['me'], true);
		return await this.client.get<UserResponse>(endpoint, { signal: signal }).then((response) => {
			if (!response.data) {
				return Promise.reject(ApiError.createFromCustomCode('unexpectedEmptyResponse'));
			}
			return Promise.resolve(this.fromResponse(response.data));
		});
	}

	/**
	 * Updates a user.
	 *
	 * @param userId The id of the user that's to be updated.
	 * @param userData The new data.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns The updated user.
	 */
	async updateUser(
		params: {
			userId: string;
			userData: Partial<User>;
			organisationId?: string | null | undefined;
		},
		signal?: AbortSignal
	): Promise<User> {
		const endpoint = this.createEndpoint('v2', [params.userId], true);
		return await this.client
			.patch<UserResponse>(
				endpoint,
				{
					clientId: params.organisationId,
					...params.userData,
				},
				{
					headers: {
						'X-Set-Fields': [
							'firstName',
							'lastName',
							'culture',
							'phoneNumber',
							'department',
							'role',
						],
					},
					signal: signal,
				}
			)
			.then((response) => {
				if (!response.data) {
					return Promise.reject(ApiError.createFromCustomCode('unexpectedEmptyResponse'));
				}
				return Promise.resolve(this.fromResponse(response.data));
			});
	}

	/**
	 * Invites a user to an organisation.
	 *
	 * @param userData The data of the User that's to be invited.
	 * @param organisationId The client id if getting it for another organisation as super admin.
	 * @param signal Signal for aborting the call.
	 *
	 * @returns The User that was invited.
	 */
	async inviteUser(
		params: { userData: Partial<User>; organisationId: string },
		signal?: AbortSignal
	): Promise<User> {
		const endpoint = this.createEndpoint('v2', [], true);
		return await this.client
			.post<UserResponse>(
				endpoint,
				{
					clientId: params.organisationId,
					...params.userData,
				},
				{
					signal: signal,
				}
			)
			.then((response) => {
				if (!response.data) {
					return Promise.reject(ApiError.createFromCustomCode('unexpectedEmptyResponse'));
				}
				return Promise.resolve(this.fromResponse(response.data));
			});
	}

	/**
	 * Model parsing.
	 */
	private fromResponse(response: UserResponse): User {
		const { id, role, clientId, clientName, ...rest } = response;

		return {
			...rest,
			id: id.toString(),
			organisationId: clientId?.toString() ?? '',
			organisationName: clientName ?? '',
			role: role as UserRoleEnum,
		};
	}
}

interface UserResponse {
	id: number;
	emailAddress: string;
	clientId?: number | null;
	clientName?: string;
	role: string;
	firstName: string;
	lastName: string;
	phoneNumber?: string;
	culture: string;
	department?: string;
}

export default UsersService;
